import React from 'react';
import * as auth from '../lib/portalAuthApi';
import { PortalApiError } from '../lib/portalHttp';
import { subscribe } from '../lib/portalDb';
import { track } from '../lib/analytics';
import type { OrgMembership, OrgRole, PortalUser, Session } from '../types/portal';

/**
 * Portal session state. One login covers every organisation and product the
 * person belongs to; the role is looked up per organisation, never globally.
 */

interface PortalAuthValue {
  session: Session | null;
  user: PortalUser | null;
  memberships: OrgMembership[];
  /** True until the stored session has been checked on first load. */
  loading: boolean;
  roleIn: (orgId: string) => OrgRole | null;
  requestMagicLink: (email: string) => Promise<{
    ok: boolean;
    previewLink?: string;
    error?: string;
  }>;
  verify: (email: string, token: string) => Promise<{
    ok: boolean;
    needsOnboarding?: boolean;
    error?: string;
  }>;
  completeOnboarding: (input: {
    firstName: string;
    lastName: string;
    jobTitle: string;
  }) => Promise<{
    ok: boolean;
    errors?: Record<string, string>;
    error?: string;
  }>;
  signOut: () => Promise<void>;
}
const PortalAuthContext = React.createContext<PortalAuthValue | null>(null);

// Refresh a minute before the 15 minute access token expires.
const REFRESH_EARLY_MS = 60_000;
export function PortalAuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let active = true;
    auth.me().then(restored => {
      if (!active) return;
      setSession(restored);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  // Silent refresh before expiry.
  React.useEffect(() => {
    if (!session) return;
    const wait = Math.max(5_000, session.expiresAt - Date.now() - REFRESH_EARLY_MS);
    const timer = window.setTimeout(() => {
      auth.refresh().then(setSession);
    }, wait);
    return () => window.clearTimeout(timer);
  }, [session]);

  // Membership changes (a role change, a removal) take effect without a reload.
  React.useEffect(() => {
    return subscribe(() => {
      const current = auth.getSession();
      if (!current) return;
      const fresh = auth.membershipsFor(current.user.id);
      if (JSON.stringify(fresh) !== JSON.stringify(current.orgMemberships)) {
        current.orgMemberships = fresh;
        setSession({
          ...current
        });
      }
    });
  }, []);
  const value = React.useMemo<PortalAuthValue>(() => {
    const failure = (error: unknown) => error instanceof PortalApiError ? error.message : 'Something went wrong. Please try again.';
    return {
      session,
      user: session?.user ?? null,
      memberships: session?.orgMemberships ?? [],
      loading,
      roleIn: orgId => session?.orgMemberships.find(m => m.orgId === orgId)?.role ?? null,
      requestMagicLink: async email => {
        try {
          const result = await auth.requestMagicLink(email);
          return {
            ok: true,
            previewLink: result.previewLink
          };
        } catch (error) {
          return {
            ok: false,
            error: failure(error)
          };
        }
      },
      verify: async (email, token) => {
        try {
          const result = await auth.verifyMagicLink(email, token);
          setSession(result.session);
          track('portal_login', {
            role: result.session.orgMemberships[0]?.role
          });
          return {
            ok: true,
            needsOnboarding: result.needsOnboarding
          };
        } catch (error) {
          return {
            ok: false,
            error: failure(error)
          };
        }
      },
      completeOnboarding: async input => {
        try {
          setSession(await auth.completeOnboarding(input));
          return {
            ok: true
          };
        } catch (error) {
          if (error instanceof PortalApiError && error.code === 'VALIDATION_ERROR') return {
            ok: false,
            errors: error.details
          };
          return {
            ok: false,
            error: failure(error)
          };
        }
      },
      signOut: async () => {
        await auth.logout();
        setSession(null);
      }
    };
  }, [session, loading]);
  return <PortalAuthContext.Provider value={value} data-id="element-1437" data-mp="2rya5pdldqdax">{children}</PortalAuthContext.Provider>;
}
export function usePortalAuth(): PortalAuthValue {
  const context = React.useContext(PortalAuthContext);
  if (!context) throw new Error('usePortalAuth must be used inside PortalAuthProvider');
  return context;
}